"use client";

import {
  Button,
  CategoryIndex,
  Chip,
  Column,
  Heading,
  Icon,
  Row,
  SearchBar,
  Spacer,
  variables,
} from "oddsgate-ds";
import { IPost } from "@/lib/wordpress/_types/archiveGrid";
import { Suspense, useState, useEffect } from "react";
import { useRouter } from "next/navigation";

import { BlocksProps } from "@/lib/wordpress/_types/blocksStyle";
import Link from "next/link";
import NavigationEvents from "@/components/common/NavigationEvents/NavigationEvents";
import displayBlock from "@/lib/wordpress/blocks/displayBlock";
import { styled } from "styled-components";
import { useGlobalContext } from "@/lib/globalContext";
import { ReplaceFrontendUrls } from "@/lib/helpers/replaceFrontendUrl";
import { WpClientRequest } from "@/lib/wordpress/wpClientRequest";

function decodeHtmlEntities(text: string): string {
  const entityMap: Record<string, string> = {
    "&#8217;": "'",
    "&amp;": "&",
    "&lt;": "<",
    "&gt;": ">",
    "&quot;": '"',
    "&#39;": "'",
    "&#8220;": '"',
    "&#8221;": '"',
    "&#8230;": "…",
  };

  return text.replace(/&#?\w+;/g, (match) => entityMap[match] ?? match);
}

const StyledRelatedItem = styled.div`
  button {
    padding: 0.625rem 2.5rem;
    font-size: 1.5rem;
    font-weight: 700;
    text-transform: none;

    @media (max-width: ${variables.responsiveMedia}) {
      height: 1.75rem;
      font-size: 1.25rem;
      padding: 0.3125rem 1.25rem;
    }
  }
`;

const GlossaryPost = (post: IPost) => {
  const state = useGlobalContext();
  const router = useRouter();

  // Search functionality state
  const [searchTerm, setSearchTerm] = useState("");

  // Category functionality state
  const [categories, setCategories] = useState<
    { name: string; slug: string }[]
  >([]);
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);

  const relatedGlossary = post?.acf?.related_glossary;
  const glossaryHeader = post?.glossaryHeader;
  const glossaryFooter = post?.glossaryFooter;
  const glossaryPage = post?.glossaryPage;
  const postType = post?.post_type;

  const BackButton = styled(Button)`
    font-size: 1.125rem;
    text-transform: none;
  `;

  type Lang = "pt" | "es" | "en";
  const lang: Lang = (state?.lang as Lang) || "en";

  // Fetch categories on component mount
  useEffect(() => {
    WpClientRequest({
      post_type: "glossary",
      variables: {
        limit: -1,
      },
    })
      .then((res) => {
        if (
          res.categories &&
          Array.isArray(res.categories) &&
          res.categories[0]?.items &&
          Array.isArray(res.categories[0].items)
        ) {
          const mappedCategories = res.categories[0].items.map((cat: any) => ({
            name: cat.name,
            slug: cat.slug,
          }));
          setCategories(mappedCategories);
        } else {
          setCategories([]);
        }
      })
      .catch((err) => {
        console.error("Failed to fetch categories", err);
        setCategories([]);
      });
  }, []);

  // Search functionality handlers
  const handleSearch = () => {
    if (searchTerm.trim()) {
      router.push(
        `${glossaryPage.slug}?search=${encodeURIComponent(searchTerm.trim())}`
      );
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      handleSearch();
    }
  };

  // Category functionality handler
  const handleCategoryChange = (categorySlug: string | null) => {
    setSelectedCategory(categorySlug);
    if (categorySlug) {
      const categorySlugUppercase =
        categorySlug.length === 1 && /[a-zA-Z]/.test(categorySlug)
          ? categorySlug.toUpperCase()
          : categorySlug;
      const finalUrl = `${glossaryPage.slug}?category=${encodeURIComponent(
        categorySlugUppercase
      )}`;
      router.push(finalUrl);
    } else {
      router.push(`${glossaryPage.slug}`);
    }
  };

  return (
    <div>
      <Suspense>
        <NavigationEvents
          translatedURI={post?.translatedURI}
          acf={{
            page_style: "dark",
            pageTitle: `${state?.dict?.post_types?.[postType] ?? "Glossary"}: ${post.title}`,
          }}
        />
      </Suspense>
      <Spacer customHeight={"6rem"} />
      <Row>
        <Column md={1}></Column>
        <Column md={10}>
          <Row>
            <Column md={1}></Column>
            <Column md={12}>
              <div>
                {!!(glossaryHeader?.blocks as [])?.length &&
                  (glossaryHeader?.blocks as []).map(
                    (block: BlocksProps, index: number) => {
                      return displayBlock(block, index);
                    }
                  )}
              </div>
              <SearchBar
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder={
                  state?.dict?.glossary?.searchPlaceholder || "Procurar..."
                }
                onClick={handleSearch}
                onKeyDown={handleKeyDown}
              />
              <div
                style={{
                  display: "flex",
                  justifyContent: "center",
                }}
              >
                <CategoryIndex
                  categories={categories}
                  selectedCategory={selectedCategory}
                  onChange={handleCategoryChange}
                />
              </div>
            </Column>
            <Column md={1}></Column>
          </Row>
          <Row className="mt-10">
            <Column>
              <BackButton
                variant="link"
                leftIcon={<Icon icon="icon-arrow-left" />}
                href={glossaryPage?.slug || "/glossary"}
                hideIconOnMobile={false}
              >
                {state?.dict?.glossary?.goBack || "Back to glossary"}
              </BackButton>
            </Column>
          </Row>
          <Row className="mt-8">
            <Column>
              <Heading tag="h1" size="h2" className="color-primary50">
                {post.title}
              </Heading>
            </Column>
          </Row>
          <Spacer customHeight={"1.5rem"} />
          <Row>
            <Column>
              {!!(post?.blocks as [])?.length &&
                (post?.blocks as []).map(
                  (block: BlocksProps, index: number) => {
                    return displayBlock(block, index);
                  }
                )}
            </Column>
          </Row>

          <Spacer height={11} />

          {relatedGlossary && relatedGlossary.length > 0 && (
            <Row>
              <Column md={12}>
                <Heading tag="h4" className="color-secondary50 fw-bold">
                  {state?.dict?.glossary?.related_glossary || "Related terms"}
                </Heading>
                <Spacer height={6} />
                <div className="d-flex gap-6 flex-flow-row flex-wrap">
                  {relatedGlossary.map((related: IPost, i: number) => {
                    return (
                      <StyledRelatedItem key={`related-${i}`}>
                        <Link
                          href={ReplaceFrontendUrls(related.slug as string)}
                        >
                          <Button
                            variant="primary"
                            iconAnimation={false}
                            rightIcon={<></>}
                          >
                            {decodeHtmlEntities(related.title as string)}
                          </Button>
                        </Link>
                      </StyledRelatedItem>
                    );
                  })}
                </div>
              </Column>
            </Row>
          )}
          <Spacer height={11} />
        </Column>
        <Column md={1}></Column>
      </Row>

      <div>
        {!!(glossaryFooter?.blocks as [])?.length &&
          (glossaryFooter?.blocks as []).map(
            (block: BlocksProps, index: number) => {
              return displayBlock(block, index);
            }
          )}
      </div>
    </div>
  );
};
export default GlossaryPost;
